import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import FloatingCart from "@/components/FloatingCart";
import { trackWaClick } from "@/lib/api";
import { motion } from "framer-motion";
import { Shirt, Coffee, KeyRound, Image as ImageIcon, Footprints, Flag, Sticker, Frame, Upload, ShoppingBag, Truck, ArrowLeft, CheckCircle2, MessageCircle } from "lucide-react";

const SERVICES = [
  { icon: KeyRound, name: "Gantungan Kunci Custom", desc: "Akrilik 5–10 cm, cetak full color dua sisi.", lead: "± 1 jam", price: "Mulai Rp 15.000" },
  { icon: Shirt, name: "Baju DTF Custom Satuan", desc: "Kaos combed 30s, sablon DTF full color. Satuan pun jadi.", lead: "± 1 hari", price: "Mulai Rp 85.000" },
  { icon: Coffee, name: "Mug Custom", desc: "Keramik 11/15 oz, sublimasi full wrap foto/logo.", lead: "± 3 jam", price: "Mulai Rp 45.000" },
  { icon: Frame, name: "Hiasan Dinding Custom", desc: "Kayu MDF 20–40 cm, cetak UV tahan pudar.", lead: "± 2 hari", price: "Mulai Rp 95.000" },
  { icon: ImageIcon, name: "Print Foto", desc: "Ukuran 3R–16R, kertas glossy/dof premium.", lead: "± 1 jam", price: "Mulai Rp 8.000" },
  { icon: Footprints, name: "Sandal Jepit Grafir", desc: "Grafir laser nama/logo, couple & souvenir.", lead: "± 1 hari", price: "Mulai Rp 55.000" },
  { icon: Flag, name: "Spanduk Flexi", desc: "Flexi Korea 280gr hi-res, indoor/outdoor.", lead: "± 2 jam", price: "Rp 25.000/m²" },
  { icon: Sticker, name: "Sticker Vinyl Die-Cut", desc: "Waterproof outdoor, potong sesuai bentuk desain.", lead: "± 1 jam", price: "Mulai Rp 5.000" },
];

const PORTFOLIO = [
  { img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=500&q=80", title: "Kaos DTF Komunitas", tag: "Baju DTF" },
  { img: "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?auto=format&fit=crop&w=500&q=80", title: "Mug Wedding Souvenir", tag: "Mug Custom" },
  { img: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=500&q=80", title: "Print Foto Keluarga", tag: "Print Foto" },
  { img: "https://images.unsplash.com/photo-1618172193763-c511deb635ca?auto=format&fit=crop&w=500&q=80", title: "Sticker Brand Kuliner", tag: "Sticker Vinyl" },
  { img: "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?auto=format&fit=crop&w=500&q=80", title: "Wall Decor Kafe", tag: "Hiasan Dinding" },
  { img: "https://images.unsplash.com/photo-1611095973763-414019e72400?auto=format&fit=crop&w=500&q=80", title: "Spanduk Grand Opening", tag: "Spanduk" },
];

const STEPS = [
  { icon: ShoppingBag, title: "1. Pilih Produk & Ukuran", desc: "Pilih produk FAS Printing di katalog, tentukan varian/ukuran sesuai kebutuhan." },
  { icon: Upload, title: "2. Upload Desain Saat Checkout", desc: "Lampirkan file desain (JPG/PNG/PDF maks 10MB) langsung di halaman checkout. Belum punya desain? Tulis konsepnya di catatan, tim kami bantu buatkan." },
  { icon: CheckCircle2, title: "3. Kami Cek & Konfirmasi", desc: "Admin warung memeriksa resolusi & kelayakan desain, lalu konfirmasi via WhatsApp sebelum cetak." },
  { icon: Truck, title: "4. Cetak & Antar", desc: "Pesanan diproduksi sesuai lead time (1 jam – 2 hari) lalu diantar ke rumah atau diambil di warung." },
];

export default function Printing() {
  return (
    <div className="min-h-screen paper-bg">
      <Navbar />
      <CartDrawer />
      <FloatingCart />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-8 pb-4">
        <Link to="/" data-testid="back-to-home-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-700/25 hover:bg-emerald-800 hover:shadow-lg hover:-translate-y-0.5 transition">
          <ArrowLeft className="w-4 h-4" /> Kembali Belanja
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-4 relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-700 to-indigo-900 text-white p-8 md:p-12"
        >
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 15% 25%, rgba(56,189,248,0.5), transparent 40%), radial-gradient(circle at 85% 75%, rgba(245,158,11,0.35), transparent 45%)" }} />
          <div className="relative max-w-2xl">
            <span className="tag-pill bg-amber-400 text-sky-950">FAS Printing 🎨</span>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Cetak custom, <span className="text-sky-300">satuan pun jadi.</span>
            </h1>
            <p className="mt-5 text-sky-100/90 text-sm md:text-base leading-relaxed max-w-lg">
              Baju DTF, mug, gantungan kunci, spanduk, sticker vinyl, print foto & hiasan dinding — semua dikerjakan tetangga sendiri, selesai mulai dari 1 jam.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/"
                data-testid="printing-shop-cta"
                className="px-6 py-3 rounded-full bg-amber-400 text-sky-950 font-bold hover:bg-amber-300 transition shadow-lg shadow-amber-400/30"
              >
                Order Sekarang →
              </Link>
              <a href="#cara-order" className="px-6 py-3 rounded-full bg-white/10 border border-white/20 font-semibold hover:bg-white/20 transition">
                Cara Order Desain
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-widest text-sky-100/80">
              <span>✓ Diskon 10% Baju DTF</span>
              <span>✓ Gratis konsultasi desain</span>
              <span>✓ Antar ke rumah</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900">Layanan Cetak</h2>
        <p className="text-stone-600 mt-1">Semua bisa custom — kirim desainmu saat checkout.</p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="rounded-3xl bg-white border border-[#E5DECF] p-5 card-tilt"
              data-testid={`printing-service-${i}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-sky-100 text-sky-700 flex items-center justify-center">
                <s.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-3 font-display font-bold text-lg leading-tight">{s.name}</h3>
              <p className="text-xs text-stone-600 mt-1 leading-relaxed">{s.desc}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="tag-pill bg-emerald-100 text-emerald-800 text-[10px]">{s.lead}</span>
                <span className="text-xs font-bold text-sky-700">{s.price}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Portfolio */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900">Portofolio Hasil Cetak</h2>
        <p className="text-stone-600 mt-1">Contoh pesanan pelanggan yang sudah jadi.</p>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          {PORTFOLIO.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="group relative rounded-3xl overflow-hidden border border-[#E5DECF] aspect-[4/3]"
              data-testid={`printing-portfolio-${i}`}
            >
              <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <span className="tag-pill bg-white/20 text-white backdrop-blur text-[10px]">{p.tag}</span>
                <div className="mt-1 font-display font-bold text-white text-sm md:text-base leading-tight">{p.title}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Cara Order */}
      <section id="cara-order" className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white p-8 md:p-10">
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Cara Order Desain Custom</h2>
          <p className="text-emerald-100/90 mt-2 max-w-xl">Gampang banget — 4 langkah dan pesananmu langsung dikerjakan.</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl bg-white/10 border border-white/15 p-5"
                data-testid={`printing-step-${i}`}
              >
                <s.icon className="w-6 h-6 text-amber-300" />
                <h3 className="mt-3 font-display font-bold text-base leading-tight">{s.title}</h3>
                <p className="text-xs text-emerald-100/85 mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <Link
            to="/"
            data-testid="printing-bottom-cta"
            className="mt-8 inline-block px-8 py-3 rounded-full bg-amber-400 text-emerald-950 font-bold hover:bg-amber-300 transition"
          >
            Mulai Order Cetak →
          </Link>
        </div>
      </section>

      <footer className="relative border-t border-[#E5DECF] py-6 text-center text-xs text-stone-500 font-mono uppercase tracking-widest">
        <a data-testid="footer-wa-link" onClick={() => trackWaClick("printing")} href="https://wa.me/6281377114000?text=Halo%20Admin%20FAS%20Group%2C%20saya%20mau%20tanya%20FAS%20Printing..." target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-bold normal-case tracking-normal hover:bg-emerald-800 transition">
          <MessageCircle className="w-4 h-4" /> Admin FAS Group Corp.
        </a>
        <div>FAS Printing by FASMART · Cetak custom dari warung tetangga</div>
        <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/kpyu0nvc_ChatGPT%20Image%2026%20Agu%202026%2C%2012.19.43.png" alt="FAS Group Corp" data-testid="footer-fasgroup-logo" className="mx-auto mt-4 h-14 w-auto object-contain rounded-xl bg-white p-1.5 border border-[#E5DECF] md:absolute md:right-6 md:bottom-4 md:m-0 md:h-16" />
      </footer>
    </div>
  );
}
